import dayjs from "dayjs";
import { Download } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  useCostingMovementDailyFg,
  useCostingMovementDailyMp,
} from "~/hooks/costing";
import { exportCsv } from "~/utils/csv";
import type { CostingItemKind } from "~/types/costing";

import { fmtCLP, fmtQty, oilRowToKg } from "./CosteoShared";

export interface DailyMovementItem {
  kind: CostingItemKind;
  id: string;
  code: string | null;
  name: string | null;
  unit?: string | null;
  /** oil_types.density_kg_per_liter — when set, daily quantities are shown in kg. */
  density?: number | null;
}

// Oil daily-quantity columns converted L→kg for display (see oilRowToKg).
const DAILY_MP_QTY_FIELDS = [
  "opening_qty",
  "purchases_qty",
  "availability_qty",
  "consumption_qty",
  "closing_qty",
] as const;

interface DailyMovementDialogProps {
  period: string;
  item: DailyMovementItem | null;
  onClose: () => void;
}

const fmtDay = (d: string) => dayjs(d).format("DD/MM");

/** Per-product daily moving-average drill-down (Inventario inicial + ingreso =
 *  disponibilidad − salida ⇒ inventario final, al costo promedio móvil del día).
 *  Mirrors the reviewer's "Días" sheet. Finished goods show ventas/margen;
 *  ingredients show compras/consumo. */
export function DailyMovementDialog({
  period,
  item,
  onClose,
}: DailyMovementDialogProps) {
  const isFg = item?.kind === "finished_good";

  const fg = useCostingMovementDailyFg(
    period,
    isFg ? (item?.id ?? null) : null
  );
  const mp = useCostingMovementDailyMp(period, item && !isFg ? item.id : null);

  const isLoading = isFg ? fg.isLoading : mp.isLoading;
  const fgRows = fg.data ?? [];
  // Aceites: show daily quantities in kg (× density) to match the MP report.
  const mpRows = (mp.data ?? []).map((r) =>
    oilRowToKg(r, item?.density, DAILY_MP_QTY_FIELDS)
  );

  const handleExport = () => {
    if (!item) return;
    if (isFg) {
      exportCsv(
        `movimiento_diario_${item.code ?? item.id}_${period}.csv`,
        [
          "Día",
          "Inv. Inicial (Cant)",
          "Inv. Inicial ($)",
          "Producción (Cant)",
          "Producción ($)",
          "Disponibilidad (Cant)",
          "Disponibilidad ($)",
          "Costo Promedio Móvil",
          "Ventas (Cant)",
          "Devoluciones (Cant)",
          "Costo de Ventas (Cant)",
          "Costo de Ventas ($)",
          "Inv. Final (Cant)",
          "Inv. Final ($)",
          "Ingresos Netos ($)",
          "Margen ($)",
          "Margen %",
        ],
        fgRows.map((r) => [
          fmtDay(r.movement_date),
          r.opening_qty,
          r.opening_value,
          r.production_qty,
          r.production_value,
          r.availability_qty,
          r.availability_value,
          r.wac_clp,
          r.sales_qty,
          r.returns_qty,
          r.cost_of_sales_qty,
          r.cost_of_sales_value,
          r.closing_qty,
          r.closing_value,
          r.net_revenue,
          r.margin,
          r.margin_pct,
        ])
      );
    } else {
      exportCsv(
        `movimiento_diario_${item.code ?? item.id}_${period}.csv`,
        [
          "Día",
          "Inv. Inicial (Cant)",
          "Inv. Inicial ($)",
          "Compras (Cant)",
          "Compras ($)",
          "Disponibilidad (Cant)",
          "Disponibilidad ($)",
          "Costo Promedio Móvil",
          "Consumo (Cant)",
          "Consumo ($)",
          "Inv. Final (Cant)",
          "Inv. Final ($)",
        ],
        mpRows.map((r) => [
          fmtDay(r.movement_date),
          r.opening_qty,
          r.opening_value,
          r.purchases_qty,
          r.purchases_value,
          r.availability_qty,
          r.availability_value,
          r.wac_clp,
          r.consumption_qty,
          r.consumption_value,
          r.closing_qty,
          r.closing_value,
        ])
      );
    }
  };

  const colCount = isFg ? 10 : 7;

  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] max-w-[95vw] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            Movimiento diario · {item?.name}
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              {item?.code} · {period}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={handleExport}
          >
            <Download className="mr-2 h-4 w-4" />
            Exportar CSV
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto rounded-lg border">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-muted">
              <TableRow>
                <TableHead>Día</TableHead>
                <TableHead className="text-right">Inv. Inicial</TableHead>
                <TableHead className="text-right">
                  {isFg ? "Producción" : "Compras"}
                </TableHead>
                <TableHead className="text-right">Disponibilidad</TableHead>
                <TableHead className="text-right">WAC móvil</TableHead>
                {isFg ? (
                  <>
                    <TableHead className="text-right">Ventas</TableHead>
                    <TableHead className="text-right">Devol.</TableHead>
                    <TableHead className="text-right">
                      Costo de Ventas
                    </TableHead>
                    <TableHead className="text-right">Inv. Final</TableHead>
                    <TableHead className="text-right">Margen</TableHead>
                  </>
                ) : (
                  <>
                    <TableHead className="text-right">Consumo</TableHead>
                    <TableHead className="text-right">Inv. Final</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={colCount}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : isFg ? (
                fgRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={colCount}
                      className="py-8 text-center text-muted-foreground"
                    >
                      Sin movimientos en {period}
                    </TableCell>
                  </TableRow>
                ) : (
                  fgRows.map((r, index) => (
                    <TableRow
                      key={r.movement_date}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      className={`duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both ${Number(r.closing_qty) < 0 ? "bg-red-50" : ""}`}
                    >
                      <TableCell className="font-medium">
                        {fmtDay(r.movement_date)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div>{fmtQty(r.opening_qty)}</div>
                        <div className="text-xs text-muted-foreground">
                          {fmtCLP(r.opening_value)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div>{fmtQty(r.production_qty)}</div>
                        <div className="text-xs text-muted-foreground">
                          {fmtCLP(r.production_value)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="font-medium">
                          {fmtQty(r.availability_qty)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {fmtCLP(r.availability_value)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {fmtCLP(r.wac_clp)}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmtQty(r.sales_qty)}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmtQty(r.returns_qty)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="font-medium">
                          {fmtCLP(r.cost_of_sales_value)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {fmtQty(r.cost_of_sales_qty)} u
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div
                          className={`font-medium ${
                            Number(r.closing_qty) < 0 ? "text-destructive" : ""
                          }`}
                        >
                          {fmtQty(r.closing_qty)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {fmtCLP(r.closing_value)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div
                          className={`font-medium ${
                            Number(r.margin) < 0 ? "text-destructive" : ""
                          }`}
                        >
                          {fmtCLP(r.margin)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {r.margin_pct === null
                            ? "—"
                            : `${Number(r.margin_pct).toFixed(1)}%`}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )
              ) : mpRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={colCount}
                    className="py-8 text-center text-muted-foreground"
                  >
                    Sin movimientos en {period}
                  </TableCell>
                </TableRow>
              ) : (
                mpRows.map((r, index) => (
                  <TableRow
                    key={r.movement_date}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className={`duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both ${Number(r.closing_qty) < 0 ? "bg-red-50" : ""}`}
                  >
                    <TableCell className="font-medium">
                      {fmtDay(r.movement_date)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div>{fmtQty(r.opening_qty)}</div>
                      <div className="text-xs text-muted-foreground">
                        {fmtCLP(r.opening_value)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div>{fmtQty(r.purchases_qty)}</div>
                      <div className="text-xs text-muted-foreground">
                        {fmtCLP(r.purchases_value)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="font-medium">
                        {fmtQty(r.availability_qty)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {fmtCLP(r.availability_value)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {fmtCLP(r.wac_clp)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div>{fmtQty(r.consumption_qty)}</div>
                      <div className="text-xs text-muted-foreground">
                        {fmtCLP(r.consumption_value)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div
                        className={`font-medium ${
                          Number(r.closing_qty) < 0 ? "text-destructive" : ""
                        }`}
                      >
                        {fmtQty(r.closing_qty)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {fmtCLP(r.closing_value)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
